import React, { useState, useEffect } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { RetryImg } from '../RetryImg';

interface SafeImageProps {
  src: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  fallbackText?: string;
  showError?: boolean;
}

const SafeImage: React.FC<SafeImageProps> = ({
  src,
  alt = '',
  className,
  style,
  fallbackText = 'Изображение не загружено',
  showError = true,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setError(null);
    setIsLoading(true);
  }, [src]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('Ошибка загрузки изображения:', src, e);
    setError('Не удалось загрузить изображение');
    setIsLoading(false);
  };

  const handleLoad = () => {
    setIsLoading(false);
    setError(null);
  };

  // Если это CloudFront изображение, добавляем специальную обработку
  const isCloudFrontImage = src.includes('cloudfront.net') || src.includes('amazonaws.com');

  return (
    <Box sx={{ position: 'relative' }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            zIndex: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Загрузка...
          </Typography>
        </Box>
      )}

      {error && showError && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="body2">
            {error}: {src}
          </Typography>
        </Alert>
      )}

      <RetryImg
        src={src}
        alt={alt}
        className={className}
        style={style}
        crossOrigin={isCloudFrontImage ? 'anonymous' : undefined}
        onLoad={handleLoad}
        onError={handleError}
        maxRetries={3}
        retryDelayMs={1000}
      />

      {error && !showError && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 100,
            backgroundColor: '#f5f5f5',
            border: '1px dashed #ccc',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {fallbackText}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SafeImage;
